import { Fragment, useEffect, useState, type ReactNode } from 'react'
import type { AiMessage, AgentTimelineEvent, AgentTraceEvent } from '../ai/taskStore'
import { MarkdownContent } from './MarkdownContent'

const TOOL_ICONS: Record<string, string> = {
  list_projects: 'ri-folder-list-line',
  get_project_context: 'ri-file-search-line',
  update_topic: 'ri-lightbulb-flash-line',
  create_project: 'ri-folder-add-line',
  update_project: 'ri-edit-line',
  delete_project: 'ri-delete-bin-line',
  get_creator_analytics: 'ri-bar-chart-2-line',
  get_video_analytics: 'ri-video-line',
  sync_bilibili: 'ri-refresh-line',
  parse_reference_video: 'ri-film-line',
  get_media_job: 'ri-time-line',
  bilibili_popular: 'ri-fire-line',
  general_web_search: 'ri-search-line',
  search_memory: 'ri-brain-line',
  manage_memory: 'ri-brain-2-line',
  topic_generator_tool: 'ri-lightbulb-line',
  outline_generator_tool: 'ri-file-list-3-line',
  script_generator_tool: 'ri-draft-line',
}

function readableValue(value: unknown) {
  if (value == null) return ''
  if (typeof value === 'string') return value
  try { return JSON.stringify(value, null, 2) } catch { return String(value) }
}

function toolIcon(trace: AgentTraceEvent) {
  const names = trace.tools?.length ? trace.tools : trace.tool ? [trace.tool] : []
  if (names.length !== 1) return 'ri-tools-line'
  const name = names[0]
  if (name.endsWith('_bilibili_popular')) return 'ri-fire-line'
  if (name.endsWith('_research_search')) return 'ri-search-line'
  return TOOL_ICONS[name] || 'ri-tools-line'
}

function traceDetail(trace: AgentTraceEvent, label: string) {
  if (trace.detail && trace.detail !== label) return trace.detail
  return undefined
}

function hasPayload(trace: AgentTraceEvent) {
  return trace.input !== undefined || trace.output !== undefined
}

function eventIcon(trace: AgentTraceEvent, active: boolean) {
  if (trace.type === 'context_compaction') return 'ri-sparkling-2-line'
  if (trace.status === 'error') return 'ri-error-warning-line'
  if (trace.type === 'tool_call') return toolIcon(trace)
  if (trace.type === 'tool_result') return (trace.tool || trace.tools?.length === 1) ? toolIcon(trace) : 'ri-checkbox-circle-line'
  if (active) return 'ri-loader-4-line'
  if (trace.type === 'error') return 'ri-error-warning-line'
  return 'ri-check-line'
}

function isLifecycleTrace(trace?: AgentTraceEvent) {
  return trace?.type === 'run_started' || trace?.type === 'run_completed'
}

export function AssistantRunDuration({ running, runStartedAt, runFinishedAt, runDurationMs }: { running: boolean; runStartedAt?: number; runFinishedAt?: number; runDurationMs?: number }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!running || runDurationMs != null) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [runDurationMs, running])
  const end = runFinishedAt ?? (running ? now : undefined)
  const durationMs = runDurationMs ?? (runStartedAt != null && end != null ? Math.max(0, end - runStartedAt) : undefined)
  if (durationMs == null) return <span className="assistant-run-time">{running ? '已处理 —' : '用时 —'}</span>
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const duration = `${minutes}m ${seconds}s`
  return <span className="assistant-run-time">{running ? `已处理 ${duration}` : `用时 ${duration}`}</span>
}

export function AssistantRunTrace({ message, children }: { message: AiMessage; children?: ReactNode }) {
  const traces = (message.trace || [])
    .filter(item => !isLifecycleTrace(item))
    .filter(item => item.label || item.summary || item.message || item.detail)
    .filter(item => item.type !== 'tool_result' || Boolean(item.summary))
    .filter((item, index, all) => index === all.findIndex(other => other.label === item.label && other.summary === item.summary && other.detail === item.detail && other.type === item.type && other.tool === item.tool))
  const baseTimeline: AgentTimelineEvent[] = message.timeline?.length
    ? message.timeline
    : traces.map(trace => ({ kind: 'trace' as const, trace }))
  const timeline = baseTimeline.filter(item => item.kind !== 'trace' || !isLifecycleTrace(item.trace))
  const hasAnswer = timeline.some(item => item.kind === 'answer')
  return <div className="assistant-message-content" aria-label="AI 消息内容">
    {timeline.map((item, timelineIndex) => {
      const active = timelineIndex === timeline.length - 1 && message.status === 'running'
      if (item.kind === 'answer') return <Fragment key={`answer-${item.seq ?? timelineIndex}`}>{item.text ? <MarkdownContent text={item.text} className="assistant-run-answer" /> : children}</Fragment>
      if (item.kind === 'reasoning') {
        return <details className={`assistant-run-reasoning-item ${active ? 'is-current' : ''}`} key={`reasoning-${item.seq ?? timelineIndex}`}>
          <summary><span>{active ? '正在思考中' : '已思考'}</span></summary>
          {item.text && <p>{item.text}</p>}
        </details>
      }
      const trace = item.trace
      if (!trace) return null
      const label = trace.label || trace.summary || trace.message || trace.detail || '正在处理'
      const detail = traceDetail(trace, label)
      const payload = hasPayload(trace)
      const eventClassName = `assistant-run-event ${trace.type === 'context_compaction' ? 'is-summary' : ''} ${trace.status === 'error' ? 'is-error' : ''} ${active ? 'is-current' : ''}`
      const eventContent = <>
        <i className={eventIcon(trace, active)} aria-hidden="true" />
        <span><b>{label}</b>{detail && <small>{detail}</small>}</span>
      </>
      if (payload) {
        return <details className={`${eventClassName} assistant-run-event-expandable`} key={`${trace.type}-${item.seq ?? timelineIndex}-${label}`}>
          <summary>{eventContent}</summary>
          <pre>{readableValue({ input: trace.input, output: trace.output })}</pre>
        </details>
      }
      return <div className={eventClassName} key={`${trace.type}-${item.seq ?? timelineIndex}-${label}`}>
        {eventContent}
      </div>
    })}
    {!hasAnswer && children}
  </div>
}
